import { defineAsyncComponent } from "vue"

import { type TableConfig } from "@/types"
import {
  type Characteristic,
  type GroupContract,
  type Child,
  Relation,
  BookingTypeEnum,
  BookingTimeCategory,
  BookingDaysEnum,
  Enrollment,
  type Booking,
  type CreateContractInput,
  PaymentMethod,
  ContributionType,
  FunctionalRole,
} from "@/graphql/types"
import { i18n } from "@/i18n"
import { type MappedChild } from "@/store/child"
import { getBookingTypeEnum, getBookingTypeEnumFromObject } from "@/utils/bookings"
import { getLatestContract } from "@/utils/child"
import { genderOptions } from "@/utils/commonOptions"
import { getOptions } from "@/utils/composables/useDropdown"
import { formatDate } from "@/utils/date"
import { type BookingInput } from "@/views/admin/migration/factories/BookingFactory"
import { type CustodianInput } from "@/views/admin/migration/factories/CustodianFactory"

export enum MaritalStatus {
  LP = "LP", // eingetragene Lebenspartnerschaft
  GS = "GS", // geschieden
  GL = "GL", // getrennt lebend
  LD = "LD", // ledig
  NV = "NV", // nicht verheiratet
  VH = "VH", // verheiratet
  VW = "VW", // verwitwet
  WG = "WG", // wiederverheiratet / geschieden
  WW = "WW", // wiederverheiratet / verwitwet
}

export enum Beitragsschema {
  elternbeitrag = "elternbeitrag",
  ferienbuchungen = "ferienbuchungen",
  notbetreuung = "notbetreuung",
  essensgeld = "essensgeld",
}

export enum Beitragszahler {
  JobcenterMemmingen = "0",
  Stiftung = "1",
}

export const Beitragsart = {
  "0": "0",
  "1": "1",
  "2": "2",
  "3": "3",
  "4": "4",
  "5": "5",
  "6": "6",
  "7": "7",
  "8": "8",
  "9": "9",
  "10": "10",
  "11": "11",
  "12": "12",
  "13": "13",
  "14": "14",
  "15": "15",
  "16": "16",
  "17": "17",
  "18": "18",
}

export const SortedCategoryOrder: BookingTimeCategory[] = [
  BookingTimeCategory.Zero,
  BookingTimeCategory.One,
  BookingTimeCategory.Two,
  BookingTimeCategory.Three,
  BookingTimeCategory.Four,
  BookingTimeCategory.Five,
  BookingTimeCategory.Six,
  BookingTimeCategory.Seven,
  BookingTimeCategory.Eight,
  BookingTimeCategory.Nine,
  BookingTimeCategory.Ten,
  BookingTimeCategory.Eleven,
  BookingTimeCategory.Twelve,
]

export const ContributionTypeValues: Record<ContributionType, string> = {
  [ContributionType.TotalAmount]: "€",
  [ContributionType.AmountPerUnit]: "€/M",
  [ContributionType.Percentage]: "%",
  [ContributionType.RemainingAmount]: "=",
}

export const childRelationOptions = getOptions(Relation, "bezugsperson")
export const maritalStatusOptions = getOptions(MaritalStatus, "marital_status")
export const bookingTypeOptions = getOptions(
  BookingTypeEnum,
  "kinder.kinder.view.reservation.types"
)
export const bookingTimeCategoryOptions = getOptions(
  BookingTimeCategory,
  "kinder.kinder.view.reservation.categorie_types"
)

export const sortedBookingTimeCategoryOptions = SortedCategoryOrder.map((code) => {
  const option = bookingTimeCategoryOptions.find((opt) => opt.code === code)
  return option || { name: "", code }
})

export const bookingDaysPolicyOptions = getOptions(
  BookingDaysEnum,
  "kinder.kinder.view.reservation.booking_day_policy"
)
export const bookingEnrollmentOptions = getOptions(
  Enrollment,
  "kinder.kinder.view.reservation.enrollements"
)

export const beitragsschemaOptions = getOptions(Beitragsschema, "beitragsschema")
export const beitragszahlerOptions = getOptions(Beitragszahler, "beitragszahler")
export const paymentMethodOptions = getOptions(PaymentMethod, "payment_methods")
export const beitragsartOptions = getOptions(Beitragsart, "beitragsart")
export const contributionTypeOptions = getOptions(ContributionType, "contribution_type")

const config: TableConfig<Child, MappedChild> = {
  columnDefs: [
    { key: "childId", header: i18n.global.t("labels.id") },
    { key: "person.lastname", header: i18n.global.t("kinder.kinder.felder.nachname.label") },

    { key: "person.firstname", header: i18n.global.t("kinder.kinder.felder.vorname.label") },
    {
      key: "person.gender",
      format: (child) => child.person?.gender && i18n.global.t(`genders.${child.person?.gender}`),
      header: i18n.global.t("kinder.kinder.felder.geschlecht.label"),
    },
    { key: "address.street", header: i18n.global.t("kinder.kinder.felder.strasse.label") },
    { key: "address.city", header: i18n.global.t("kinder.kinder.felder.ort.label") },
    { key: "address.zipCode", header: i18n.global.t("kinder.kinder.felder.plz.label") },
    {
      key: "address.country",
      format: (child) => {
        const countryCode = child.address?.country
        if (!countryCode) return ""

        const key = `countries_code.${countryCode}`
        return i18n.global.te(key) ? i18n.global.t(key) : countryCode
      },
      header: i18n.global.t("kinder.kinder.felder.land.label"),
      active: false,
    },
    {
      key: "birth.date",
      format: (child) => child.birth?.date && formatDate(child.birth?.date),
      header: i18n.global.t("kinder.kinder.felder.geburtsdatum.label"),
    },
    { key: "contact.phone", header: i18n.global.t("kinder.kinder.felder.telefon.label") },
  ],
  filterableColumnDefs: [
    {
      key: "person.firstname", //default
      dataKey: "person.firstname",
      header: i18n.global.t("kinder.kinder.page.filter.felder.vorname.label"),
      filterType: "string",
    },
    {
      key: "person.lastname", //default
      dataKey: "person.lastname",
      header: i18n.global.t("kinder.kinder.page.filter.felder.nachname.label"),
      filterType: "string",
    },
    {
      key: "plz",
      dataKey: "address.zipCode",
      header: i18n.global.t("kinder.kinder.page.filter.felder.plz.label"),
      filterType: "string",
    },
    {
      key: "ort",
      dataKey: "address.city",
      header: i18n.global.t("kinder.kinder.page.filter.felder.ort.label"),
      filterType: "string",
    },
    {
      key: "ortsteil",
      dataKey: "address.district",
      header: i18n.global.t("kinder.kinder.page.filter.felder.ortsteil.label"),
      filterType: "string",
    },
    {
      key: "groups", //default
      dataKey: "childGroups",
      fields: ["contracts.groups.group.id", "contracts.groups.group.name"],
      requiredPermissions: ["group:list"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.gruppe.label"),
      filterType: "select",
      matchModes: ["contains"],
      component: defineAsyncComponent(() => import("@/components/context/GroupsSelect.vue")),
    },
    {
      key: "aktion_gruppe",
      dataKey: "childActionGroups",
      requiredPermissions: ["group:list"],
      fields: [
        "contracts.groups.group.id",
        "contracts.groups.group.name",
        "contracts.groups.group.actionGroup",
      ],
      header: i18n.global.t("kinder.kinder.page.filter.felder.aktion_gruppe.label"),
      filterType: "select",
      matchModes: ["contains"],
      component: defineAsyncComponent(() => import("@/components/context/ActionGroupsSelect.vue")),
    },
    {
      key: "characteristics",
      dataKey: "characteristicIds",
      requiredPermissions: ["characteristic:list"],
      fields: ["characteristics.characteristic.id"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.merkmale.label"),
      filterType: "select",
      matchModes: ["contains"],
      component: defineAsyncComponent(
        () => import("@/components/context/CharacteristicsSelect.vue")
      ),
    },
    {
      key: "anmeldung",
      dataKey: "contractsRegistrationDates",
      fields: ["contracts.registrationDate"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.anmeldung.label"),
      filterType: "date_range",
      matchModes: ["dateRangeContains"],
    },
    {
      key: "startOfMemberships", //default
      dataKey: "startOfContractDates",
      fields: ["contracts.startOfContract"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.aufnahme.label"),
      filterType: "date_range",
      matchModes: ["dateRangeContains"],
    },
    {
      key: "endOfMemberships", //default
      dataKey: "endOfContractDates",
      fields: ["contracts.endOfContract"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.austritt.label"),
      filterType: "date_range",
      matchModes: ["dateRangeContains"],
    },
    {
      key: "age", //default
      dataKey: "age",
      header: i18n.global.t("kinder.kinder.page.filter.felder.alter.label"),
      fields: ["birth.date"],
      filterType: "number_range",
    },
    {
      key: "birth.date", //default
      dataKey: "birth.date",
      header: i18n.global.t("kinder.kinder.page.filter.felder.geburtsdatum.label"),
      filterType: "date_range",
    },
    {
      key: "konfession",
      dataKey: "person.denomination",
      header: i18n.global.t("kinder.kinder.page.filter.felder.konfession.label"),
      filterType: "select",
      component: defineAsyncComponent(() => import("@/components/context/DenominationSelect.vue")),
    },
    {
      key: "staatsangehoerigkeit",
      dataKey: "person.citizenships",
      header: i18n.global.t("kinder.kinder.page.filter.felder.staatsangehoerigkeit.label"),
      filterType: "select",
      matchModes: ["contains"],
      component: defineAsyncComponent(() => import("@/components/context/NationalitySelect.vue")),
    },
    {
      key: "anzahl_geschwister",
      dataKey: "anzahl_geschwister",
      fields: ["siblings.id", "externalSiblings.firstname"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.anzahl_geschwister.label"),
      filterType: "number_range",
    },
    {
      key: "person.gender", //default
      dataKey: "person.gender",
      header: i18n.global.t("kinder.kinder.page.filter.felder.geschlecht.label"),
      filterType: "select",
      options: genderOptions,
    },
    {
      key: "kind_id",
      dataKey: "childId",
      header: i18n.global.t("kinder.kinder.page.filter.felder.kind_id.label"),
      filterType: "string",
    },
    {
      key: "buchungskriterien",
      dataKey: "bookingsCategories",
      fields: ["contracts.bookings.type"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.buchungskriterien.label"),
      filterType: "select",
      matchModes: ["contains"],
      options: bookingTypeOptions,
    },
    {
      key: "stundenkategorie",
      dataKey: "bookingHoursCategories",
      fields: ["contracts.bookings.timeFactor.category"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.stundenkategorie.label"),
      filterType: "select",
      options: sortedBookingTimeCategoryOptions,
      disableSort: true,
    },
    {
      key: "buchungsbemerkung",
      dataKey: "bookingRemarks",
      fields: ["contracts.bookings.remarks"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.buchungsbemerkung.label"),
      filterType: "string",
    },
    {
      key: "booking_precourse",
      dataKey: "bookingPreCourse",
      fields: ["contracts.bookings.preCourse"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.vorkurskind.label"),
      filterType: "bool",
    },
    {
      key: "booking_is_pre_schooler",
      dataKey: "bookingIsPreSchooler",
      fields: ["contracts.bookings.isPreSchooler"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.vorschulkind.label"),
      filterType: "bool",
    },
    {
      key: "booking_lunch",
      dataKey: "bookingLunch",
      fields: ["contracts.bookings.lunch"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.mittagsverpflegung.label"),
      filterType: "bool",
    },
    {
      key: "booking_slot_splitting",
      dataKey: "bookingSlotSplitting",
      fields: [],
      header: i18n.global.t("kinder.kinder.page.filter.felder.platzsplitting.label"),
      filterType: "bool",
    },
    {
      key: "booking_enrollment_prematurely",
      dataKey: "bookingEnrollmentPrematurely",
      fields: ["contracts.bookings.preSchooler"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.vorzeitige_einschulung.label"),
      filterType: "bool",
    },
    {
      key: "booking_enrollment_postponed",
      dataKey: "bookingEnrollmentPostponed",
      fields: ["contracts.bookings.preSchooler"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.einschulung_zurueckgestellt.label"),
      filterType: "bool",
    },
    {
      key: "booking_commune",
      dataKey: "bookingCommunes",
      fields: [],
      header: i18n.global.t("kinder.kinder.page.filter.felder.zuschussgemeinde.label"),
      filterType: "select",
      matchModes: ["contains"],
      component: defineAsyncComponent(() => import("@/components/context/CommuneSelect.vue")),
    },
    {
      key: "fad_number",
      dataKey: "fadNumbers",
      fields: [
        "financialAccountingData.fadNumber",
        "contracts.mainDebtors.custody.custodian.financialAccountingData.fadNumber",
        "contracts.debtors.custody.custodian.financialAccountingData.fadNumber",
      ],
      header: i18n.global.t("kinder.kinder.page.filter.felder.fad_number.label"),
      filterType: "string",
      matchModes: ["contains"],
    },
    {
      key: "eligible_for_parental_fee_subsidy",
      dataKey: "eligibleForParentalFeeSubsidy",
      fields: [],
      header: i18n.global.t(
        "kinder.kinder.page.filter.felder.berechtigt_fuer_elternbeitragszuschuss.label"
      ),
      filterType: "bool",
    },
    {
      key: "family_language_not_german",
      dataKey: "person.familyLanguageIsNotGerman",
      fields: ["person.familyLanguageIsNotGerman"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.familiensprache_nicht_deutsch.label"),
      filterType: "bool",
    },
    {
      key: "allowed_to_go_home_alone",
      dataKey: "allowedToGoHomeAlone",
      fields: ["allowedToGoHomeAlone"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.kind_darf_allein_nach_haus.label"),
      filterType: "bool",
    },
    {
      key: "early_diagnostics_certified",
      dataKey: "vaccination.earlyDiagnosticsCertified",
      fields: ["vaccination.earlyDiagnosticsCertified"],
      requiredPermissions: [FunctionalRole.ChildAllowSensitiveData],
      header: i18n.global.t(
        "kinder.kinder.page.filter.felder.frueherkennungsuntersuchungen_erbracht.label"
      ),
      filterType: "bool",
    },
    {
      key: "vaccination_certified",
      dataKey: "vaccination.vaccinationCertified",
      fields: ["vaccination.vaccinationCertified"],
      requiredPermissions: [FunctionalRole.ChildAllowSensitiveData],
      header: i18n.global.t(
        "kinder.kinder.page.filter.felder.impfschutz_oder_beratung_erbracht.label"
      ),
      filterType: "bool",
    },
    {
      key: "measles_protection_fulfilled",
      dataKey: "measlesProtectionFulfilled",
      fields: ["vaccination.status"],
      requiredPermissions: [FunctionalRole.ChildAllowSensitiveData],
      header: i18n.global.t("kinder.kinder.page.filter.felder.masern_impfschutz_erfuellt.label"),
      filterType: "bool",
    },
    {
      key: "disability_certified",
      dataKey: "disability.certified",
      fields: ["disability.certified"],
      header: i18n.global.t(
        "kinder.kinder.page.filter.felder.bescheinigung_ueber_behinderung.label"
      ),
      filterType: "bool",
    },
    {
      key: "assistance_physical",
      dataKey: "disability.physical",
      fields: ["disability.physical"],
      header: i18n.global.t(
        "kinder.kinder.page.filter.felder.eingliederungshilfe_koerperliche_behinderung.label"
      ),
      filterType: "bool",
    },
    {
      key: "assistance_intellectual",
      dataKey: "disability.intellectual",
      fields: ["disability.intellectual"],
      header: i18n.global.t(
        "kinder.kinder.page.filter.felder.eingliederungshilfe_geistige_behinderung.label"
      ),
      filterType: "bool",
    },
    {
      key: "assistance_mental",
      dataKey: "disability.mental",
      fields: ["disability.mental"],
      header: i18n.global.t(
        "kinder.kinder.page.filter.felder.eingliederungshilfe_seelische_behinderung.label"
      ),
      filterType: "bool",
    },
    {
      key: "zeitbeschreibung",
      dataKey: "bookingComments",
      fields: ["contracts.bookings.times.comments"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.zeitbeschreibung.label"),
      filterType: "string",
    },
    {
      key: "schule",
      dataKey: "bookingSchools",
      fields: ["contracts.bookings.schoolData.school.id"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.schule.label"),
      filterType: "select",
      component: defineAsyncComponent(() => import("@/components/context/SchoolSelect.vue")),
    },
    {
      key: "klasse",
      dataKey: "bookingClasses",
      fields: ["contracts.bookings.schoolData.className"],
      header: i18n.global.t("kinder.kinder.page.filter.felder.klasse.label"),
      filterType: "string",
    },
  ],
}

export default config

export const SiblingTableConfig: TableConfig<Child, Child> = {
  columnDefs: [
    {
      key: "child",
      header: i18n.global.t("kinder.kinder.view.geschwister.table.headers.child"),
      format: (child) => `${child.person?.lastname ?? ""} ${child.person?.firstname ?? ""}`.trim(),
    },
    {
      key: "birthdate",
      header: i18n.global.t("labels.birthdate"),
      format: (child) => (child.birth?.date ? formatDate(child.birth.date) : ""),
    },
    {
      key: "registrationDate",
      header: i18n.global.t("kinder.kinder.view.geschwister.table.headers.registration_date"),
      format: (child: Child) => {
        const contract = getLatestContract(child)
        return contract?.registrationDate ? formatDate(contract.registrationDate) : ""
      },
    },
    {
      key: "exitDate",
      header: i18n.global.t("kinder.kinder.view.geschwister.table.headers.exit_date"),
      format: (child: Child) => {
        const contract = getLatestContract(child)
        return contract?.endOfContract ? formatDate(contract.endOfContract) : ""
      },
    },
  ],
}

export const CharacteristicTableConfig: TableConfig<Characteristic, Characteristic> = {
  columnDefs: [
    { key: "n", header: i18n.global.t("labels.id") },
    { key: "name", header: i18n.global.t("labels.name") },
  ],
}

export const MigrationCustodianTableConfig: TableConfig<CustodianInput, CustodianInput> = {
  columnDefs: [
    {
      key: "IDNR",
      header: "IDNR",
    },
    {
      key: "ADRNR",
      header: "ADRNR",
    },
    {
      key: "custodianChild.relation",
      header: i18n.global.t("kinder.kinder.felder.local_guardians.relation_to_child.label"),
      format: (data) =>
        data?.custodianChild?.relation
          ? i18n.global.t(`bezugsperson.${data.custodianChild.relation}`)
          : "",
    },
    {
      key: "custodian.person.lastname",
      header: i18n.global.t("kinder.kinder.felder.local_guardians.nachname.label"),
    },
    {
      key: "custodian.person.firstname",
      header: i18n.global.t("kinder.kinder.felder.local_guardians.vorname.label"),
    },
    {
      key: "custodian.person.title",
      header: i18n.global.t("labels.title"),
      format: (data) =>
        data?.custodian?.person?.title
          ? i18n.global.t(`titles.${data.custodian.person.title}`)
          : "",
    },
  ],
}

export const BookingTableConfig: TableConfig<Booking, Booking> = {
  columnDefs: [
    {
      key: "type",
      header: i18n.global.t("kinder.kinder.felder.bookings.buchungsart.label"),
      format: (data) =>
        i18n.global.t(
          `kinder.kinder.view.reservation.types.${data.type ?? getBookingTypeEnumFromObject(data.typeObject)}`
        ),
    },
    {
      key: "from",
      format: (data) => (data?.from ? formatDate(data.from) : ""),
      header: i18n.global.t("personal.mitarbeiter.view.booking.felder.von.label"),
    },
    {
      key: "to",
      format: (data) => (data?.to ? formatDate(data.to) : ""),
      header: i18n.global.t("personal.mitarbeiter.view.booking.felder.bis.label"),
    },
  ],
}

export const MigrationBookingTableConfig: TableConfig<BookingInput> = {
  columnDefs: [
    {
      key: "KINDNR",
      header: "KINDNR",
    },
    {
      key: "type",
      header: i18n.global.t("kinder.kinder.felder.bookings.buchungsart.label"),
      format: (data) =>
        i18n.global.t(
          `kinder.kinder.view.reservation.types.${getBookingTypeEnum(data?.input?.typeObject)}`
        ),
    },
    {
      key: "from",
      format: (data) => (data?.input?.from ? formatDate(data.input.from) : ""),
      header: i18n.global.t("personal.mitarbeiter.view.booking.felder.von.label"),
    },
    {
      key: "to",
      format: (data) => (data?.input?.to ? formatDate(data.input.to) : ""),
      header: i18n.global.t("personal.mitarbeiter.view.booking.felder.bis.label"),
    },
  ],
}

export const GroupTableConfig: TableConfig<GroupContract, GroupContract> = {
  columnDefs: [
    { key: "group.name", header: i18n.global.t("kinder.kinder.felder.gruppen.gruppe.label") },
    {
      key: "movement.entry",
      header: i18n.global.t("kinder.kinder.felder.gruppen.von.label"),
      format: (data) => (data.movement.entry ? formatDate(data.movement.entry) : ""),
    },
    {
      key: "movement.exit",
      header: i18n.global.t("kinder.kinder.felder.gruppen.bis.label"),
      format: (data) => (data.movement.exit ? formatDate(data.movement.exit) : ""),
    },
    { key: "notes", header: i18n.global.t("kinder.kinder.felder.gruppen.anmerkungen.label") },
  ],
}

export const ContractTableConfig: TableConfig<CreateContractInput, CreateContractInput> = {
  columnDefs: [
    {
      key: "KINDNR",
      header: "KINDNR",
    },
    {
      key: "registrationDate",
      header: i18n.global.t("kinder.kinder.view.contract.felder.registrationDate.label"),
    },
    {
      key: "startOfContract",
      header: i18n.global.t("kinder.kinder.felder.vertrag.laufzeit_von.label"),
      format: (data) => (data.startOfContract ? formatDate(data.startOfContract) : ""),
    },
    {
      key: "endOfContract",
      header: i18n.global.t("kinder.kinder.felder.vertrag.laufzeit_bis.label"),
      format: (data) => (data.endOfContract ? formatDate(data.endOfContract) : ""),
    },
    { key: "remarks", header: i18n.global.t("kinder.kinder.view.contract.felder.remarks.label") },
  ],
}
